import {AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterLink, RouterOutlet} from "@angular/router";
import {RouterLinkDirective} from "./directives/router-link.directive";
import {LoadingService} from "./services/loading/loading.service";
import {delay, filter, map, mergeMap} from "rxjs/operators";
import {AuthService} from "./services/auth/auth.service";
import {TokenRefreshService} from "./services/auth/token-refresh.service";
import {SidebarService} from "./services/sidebar/sidebar.service";
import {VERSION} from "../environments/version";
import {OverlayContainer} from "@angular/cdk/overlay";
import {Organisation, Project, ProjectControllerService, User} from "./api";
import {StateConfigService} from "./services/state/state-config.service";
import {
  faArrowRightFromBracket,
  faBadgeCheck,
  faBagShopping,
  faBuilding,
  faChartPieSimple,
  faChartSimple,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faCircleInfo,
  faClipboardCheck,
  faClipboardList,
  faCube,
  faCubes,
  faDiagramProject,
  faEye,
  faFileCircleCheck,
  faFileSignature,
  faFingerprint,
  faFolderOpen,
  faGauge,
  faGrid2,
  faListCheck,
  faMemoCircleCheck,
  faMemoCircleInfo,
  faShareNodes,
  faShieldCheck,
  faStamp,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
// import {faFolderOpen} from '@fortawesome/pro-solid-svg-icons'; # replaced with solid version
import {MatDialog} from "@angular/material/dialog";
import {
  OrganisationPopupComponent
} from "./components/organisations/components/organisation-popup/organisation-popup.component";
import {PopoutContentService} from "./services/popout/popout.service";
import {PermissionService} from "./services/permission/permission.service";
import {initFlowbite} from "flowbite";
import {ExchangeService} from "./services/exchange/exchange.service";
import {faChatbicAi, faIfc, faReport} from "./components/util/icons/custom-icons";
import {SidebarComponent} from "./components/util/design/sidebar/sidebar.component";
import {NavbarComponent} from "./components/util/design/navbar/navbar.component";
import {SidebarItemComponent} from "./components/util/design/sidebar/sidebar-item/sidebar-item.component";

import {SidebarFooterComponent} from "./components/util/design/sidebar/sidebar-footer/sidebar-footer.component";
import {NgClass, NgOptimizedImage} from "@angular/common";
import {ChatFabComponent} from "./components/chatBIC/chat-fab/chat-fab.component";
import {ChatService} from "./components/chatBIC/service/chat.service";
import {
  PinnedChecksSidebarComponent
} from "./components/my-checks/pinned-checks-sidebar/pinned-checks-sidebar.component";
import {BicBugBadgeComponent} from "@buildinginformationcloud/bug-bic";
import {bugBicInstanceId} from "./bug-bic-instance";
import {
  BackgroundActivityBadgeComponent
} from "./components/files/components/background-activity-badge/background-activity-badge.component";
import StateEnum = Project.StateEnum;


/**
 * Root component for application
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLinkDirective,
    SidebarComponent,
    NavbarComponent,
    SidebarItemComponent,
    RouterLink,
    SidebarFooterComponent,
    NgClass,
    RouterOutlet,
    NgOptimizedImage,
    ChatFabComponent,
    PinnedChecksSidebarComponent,
    BicBugBadgeComponent,
    BackgroundActivityBadgeComponent
  ]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  router = inject(Router);
  route = inject(ActivatedRoute);
  loadingService = inject(LoadingService);
  authService = inject(AuthService);
  private tokenRefresh = inject(TokenRefreshService);
  sidebarService = inject(SidebarService);
  overlayContainer = inject(OverlayContainer);
  dialog = inject(MatDialog);
  projectControllerService = inject(ProjectControllerService);
  stateConfigService = inject(StateConfigService);
  popoutContentService = inject(PopoutContentService);
  permissionService = inject(PermissionService);
  exchangeService = inject(ExchangeService);
  chatService = inject(ChatService);

  /**
   * Beta und Live melden in getrennte bugBIC-Instanzen; die Zuordnung kommt aus
   * dem Hostnamen (siehe bug-bic-instance.ts). Einmal beim Erzeugen ausgewertet -
   * die Domain aendert sich zur Laufzeit nicht.
   */
  readonly bugBicInstanceId = bugBicInstanceId();

  /**
   * Show the floating chatBIC FAB when logged in, project selected, and not on the full chat page.
   *
   * Waehrend eines myCheck-Quickstarts bleibt er aus: dort ist der Chat als eigene Flaeche in die
   * Seite eingebettet, und ein zweites Chatfenster darueber fuehrt nur zu zwei konkurrierenden
   * Eingaben auf derselben Konversation.
   */
  get showChatFab(): boolean {
    return this.authService.isLoggedIn()
      && this.stateConfigService.selectedProject != undefined
      && !this.chatService.isOnChatPage()
      && !this.chatService.isMycheckSurfaceActive;
  }


  /**
   * Display state of the analysis running menu
   */
  allowedApplicationForms: StateEnum[] = [StateEnum.BY, StateEnum.NW, StateEnum.BE, StateEnum.HH, StateEnum.NI, StateEnum.BB, StateEnum.HB, StateEnum.HE, StateEnum.MV];

  pageTitle = ""

  /**
   * VERSION of the Application
   */
  VERSION = VERSION;
  user!: User;
  organisation!: Organisation;

  sidebarOpened: boolean = true;
  sidebarCollapsed: boolean = false;
  hasTouchScreen: boolean = false;

  /**
   * Constructor
   */
  constructor() {
    const router = this.router;
    const sidebarService = this.sidebarService;


    router.events.subscribe((hint) => {
      sidebarService.hint = "";
      sidebarService.hintTitle = "";
      sidebarService.isProject = false;
      sidebarService.isRegister = false;
    })
  }

  ngOnDestroy(): void {
    this.stateConfigService.stop();
  }

  ngAfterViewInit() {
    initFlowbite()
  }

  private startSession(): void {
    this.stateConfigService.start();
    this.exchangeService.start();
  }

  /**
   * Component initialization
   */
  ngOnInit() {
    console.log("planBIC - " + this.VERSION.version);

    window.addEventListener('storage', (event) => {
      if (event.key === 'token' && !event.newValue) {
        this.authService.logout();
        this.router.navigateByUrl("/login");
      }
    });
    initFlowbite();
    this.detectTouchScreen();

    const screenSize = this.getScreenSize();

    if (this.authService.isLoggedIn()) {
      // Erst das Token erneuern, falls es abgelaufen ist: ist die Sitzung dahinter vorbei, meldet
      // getValidToken ab, und State/SSE sollen dann gar nicht erst anlaufen. Ist nur das Backend
      // nicht erreichbar, trotzdem starten – jeder Request versucht den Refresh dann selbst erneut.
      this.tokenRefresh.getValidToken().then(
        token => {
          if (token) {
            this.startSession();
          }
        },
        error => {
          console.error('Token konnte beim Start nicht erneuert werden:', error);
          this.startSession();
        }
      );
    }


    // Updates sidebar Title on navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.rootRoute(this.route)),
      filter((route: ActivatedRoute) => route.outlet === 'primary'),
      mergeMap((route: ActivatedRoute) => route.data)
    ).subscribe((event: { [name: string]: any }) => {
      this.sidebarService.title = event['title'];
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.navigationTrigger === 'popstate') {
          if(event.url == '/projects') {
            this.stateConfigService.selectProject(undefined, false);
          }
          if(event.url == '/organisations') {
            this.stateConfigService.selectOrganisation(undefined, false, false, true);
          }
        }
      }
    });

    this.listenToLoading();

    this.sidebarCollapsed = localStorage.getItem("sidebarCollapsed") === "true";
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowClose(event: Event) {
    this.popoutContentService.close();
  }

  /**
   * Activates LoadingService in combination with HTTP Interceptor
   */
  listenToLoading(): void {
    this.loadingService.loadingSub
      .pipe(delay(0))
      .subscribe((loading) => {
        this.loadingService.isLoading = loading;
      });
  }

  /**
   * Gets the root for given route
   * @param route given actual route
   * @private
   */
  private rootRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  applicationFromActivated() {
    return this.allowedApplicationForms.includes(this.stateConfigService.selectedProject.state!)
  }

  setSidebarStatus(status: boolean) {
    this.sidebarCollapsed = status;
    localStorage.setItem("sidebarCollapsed", String(status));
  }

  openNewOrganisation() {
    let ref = this.dialog.open(OrganisationPopupComponent, {
      panelClass: 'rounded-corners-dialog',
      data: {
        title: "Neues Unternehmen"
      },
      width: '60%',
    });
  }


  detectTouchScreen() {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroidTablet = /Android/.test(userAgent) && !/Mobile/.test(userAgent);
    const isTablet = isIOS || isAndroidTablet; // Basic check for iOS and Android tablets

    const likelyDesktopOrLaptop = window.innerWidth >= 768;

    if (!isTablet && !likelyDesktopOrLaptop) {
      if ('maxTouchPoints' in navigator) {
        this.hasTouchScreen = navigator.maxTouchPoints > 0;
      } else {
        const matchMedia = window.matchMedia('(pointer: coarse)');
        if (matchMedia.matches) {
          this.hasTouchScreen = true;
        } else if ('orientation' in window) {
          this.hasTouchScreen = true; // Deprecated but can be a good indication
        } else {
          // Using userAgent as a last resort
          const userAgent = (navigator as any).userAgent;
          this.hasTouchScreen = (
            /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(userAgent) ||
            /\b(Android|Windows Phone|iPod)\b/i.test(userAgent)
          );
        }
      }
    } else {
      this.hasTouchScreen = false; // Explicitly exclude tablets
    }
  }


  openEditOrganisation(organisationId: number) {
    let ref = this.dialog.open(OrganisationPopupComponent, {
      panelClass: 'rounded-corners-dialog',
      data: {
        organisationId: organisationId,
        title: "Unternehmenseinstellungen"
      },
      width: '60%',
    });
  }

  getScreenSize(): { width: number; height: number; devicePixelRatio: number } {
    const screenSize = {
      width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
      devicePixelRatio: window.devicePixelRatio
    };

    return screenSize;
  }

  navigateWithEmptyFiles(url: (string | number) []) {
    this.router.navigate(['projects/' + this.stateConfigService.getProjectId() + '/fireprotection/ruleSelection'], {state: {files: []}}).then(() => this.router.navigate(url, {state: {files: []}}))
  }

  navigateToChat(): void {
    const currentUrl = this.router.url;
    const baseChatRoute = `/projects/${this.stateConfigService.selectedProject.id}/chat`;

    // Check if we're already on a chatBIC route (with or without chatId)
    if (currentUrl.startsWith(baseChatRoute)) {
      // Already on chatBIC route, do nothing to preserve state
      return;
    }

    // Navigate to base chatBIC route only if coming from a different route
    this.router.navigate(['projects', this.stateConfigService.selectedProject.id, 'chat']);
  }


  hasApplicationTool() {
    return this.stateConfigService.selectedProject.state === StateEnum.BY
      || this.stateConfigService.selectedProject.state === StateEnum.NW
      || this.stateConfigService.selectedProject.state === StateEnum.BE
      || this.stateConfigService.selectedProject.state === StateEnum.HH
      || this.stateConfigService.selectedProject.state === StateEnum.NI
      || this.stateConfigService.selectedProject.state === StateEnum.HE
      || this.stateConfigService.selectedProject.state === StateEnum.MV
      || this.stateConfigService.selectedProject.state === StateEnum.HB
      || this.stateConfigService.selectedProject.state === StateEnum.BB
  }


  protected readonly faArrowRightFromBracket = faArrowRightFromBracket;
  protected readonly faBadgeCheck = faBadgeCheck;
  protected readonly faBagShopping = faBagShopping;
  protected readonly faBuilding = faBuilding;
  protected readonly faChartSimple = faChartSimple;
  protected readonly faChatbicAi = faChatbicAi;
  protected readonly faCheck = faCheck;
  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faClipboardList = faClipboardList;
  protected readonly faClipboardCheck = faClipboardCheck;
  protected readonly faCube = faCube;
  protected readonly faCubes = faCubes;
  protected readonly faDiagramProject = faDiagramProject;
  protected readonly faEye = faEye;
  protected readonly faFileCircleCheck = faFileCircleCheck;
  protected readonly faFileSignature = faFileSignature;
  protected readonly faFolderOpen = faFolderOpen;
  protected readonly faGauge = faGauge;
  protected readonly faGrid2 = faGrid2;
  protected readonly faIfc = faIfc;
  protected readonly faListCheck = faListCheck;
  protected readonly faMemoCircleInfo = faMemoCircleInfo;
  protected readonly faMemoCircleCheck = faMemoCircleCheck;
  protected readonly faReport = faReport;
  protected readonly faShieldCheck = faShieldCheck;
  protected readonly faShareNodes = faShareNodes;

  /** Regelwerk-Admin (plattform-globales Privileg, per DB-Eintrag vergeben). */
  protected get isRulesetAdmin(): boolean {
    return this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN');
  }
  protected readonly faStamp = faStamp;
  protected readonly faUser = faUser;

  protected readonly Project = Project;
  protected readonly StateEnum = StateEnum;
  protected readonly window = window;
  protected readonly faFingerprint = faFingerprint;
  protected readonly faChartPieSimple = faChartPieSimple;
}
